import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PageviewTracker — beacon pageview + click heatmap globale.
 *
 * Tracking:
 *  1. Ogni route change → POST /api/track/pv (navigator.sendBeacon)
 *  2. Ogni click su elementi con data-testid → trackEvent("click", testid)
 *     (cattura in bubble phase senza wrappare i bottoni esistenti)
 *
 * GDPR: nessun cookie, ip hash con salt giornaliero, session_id solo sessionStorage.
 */
export default function PageviewTracker() {
  const location = useLocation();
  const lastPath = useRef(null);

  // Pageview beacon su route change
  useEffect(() => {
    const path = location.pathname + location.search;
    if (lastPath.current === path) return;
    lastPath.current = path;
    if (path.startsWith("/admin")) return;

    const payload = JSON.stringify({
      path: location.pathname,
      referer: document.referrer || null,
    });
    const url = `${API}/track/pv`;
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
          credentials: "omit",
        }).catch(() => {});
      }
    } catch {
      /* silenzioso */
    }
  }, [location]);

  // Click heatmap globale (delegato document-level)
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    const onClick = (e) => {
      try {
        const el = e.target.closest("[data-testid]");
        if (!el) return;
        const testId = el.getAttribute("data-testid");
        if (!testId || testId.length > 80) return;
        // Skip elementi admin-only
        if (testId.startsWith("admin-") || testId.startsWith("traffic-")) return;
        trackEvent("click", testId, {
          meta: {
            tag: el.tagName.toLowerCase(),
            text: (el.textContent || "").trim().slice(0, 60),
          },
        });
      } catch {
        /* silenzioso */
      }
    };
    document.addEventListener("click", onClick, { capture: false });
    return () => document.removeEventListener("click", onClick);
  }, []);

  return null;
}
